import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { HiOfficeBuilding } from 'react-icons/hi';
import { schoolsApi } from '../../api/schools';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';

// Nominatim response structure
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

interface SchoolData {
  schoolId: number;
  educationOrganizationId: number;
  nameOfInstitution: string;
  shortNameOfInstitution?: string | null;
  lowGradeLevelDescriptorId?: number | null;
  highGradeLevelDescriptorId?: number | null;
  schoolTypeDescriptorId?: number | null;
  enrollment?: number | null;
  mainEmailAddress?: string | null;
  webSite?: string | null;
  primaryPhone?: string | null;
}

interface EditSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  school: SchoolData;
}

interface SchoolFormData {
  nameOfInstitution: string;
  shortNameOfInstitution: string;
  lowGradeLevelDescriptorId: number | null;
  highGradeLevelDescriptorId: number | null;
  schoolTypeDescriptorId: number | null;
  enrollment: string;
  mainEmailAddress: string;
  webSite: string;
  primaryPhone: string;
  // Address fields
  streetNumberName: string;
  city: string;
  postalCode: string;
  nameOfCounty: string;
  latitude: number | null;
  longitude: number | null;
}

export const EditSchoolModal: React.FC<EditSchoolModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  school
}) => {
  const [formData, setFormData] = useState<SchoolFormData>({
    nameOfInstitution: '',
    shortNameOfInstitution: '',
    lowGradeLevelDescriptorId: null,
    highGradeLevelDescriptorId: null,
    schoolTypeDescriptorId: null,
    enrollment: '',
    mainEmailAddress: '',
    webSite: '',
    primaryPhone: '',
    streetNumberName: '',
    city: '',
    postalCode: '',
    nameOfCounty: '',
    latitude: null,
    longitude: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [existingAddressId, setExistingAddressId] = useState<number | null>(null);

  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Descriptor options
  const [gradeLevelDescriptors, setGradeLevelDescriptors] = useState<Descriptor[]>([]);
  const [schoolTypeDescriptors, setSchoolTypeDescriptors] = useState<Descriptor[]>([]);
  const [loadingDescriptors, setLoadingDescriptors] = useState(false);

  // Populate form when modal opens or school changes
  useEffect(() => {
    if (isOpen && school) {
      setFormData({
        nameOfInstitution: school.nameOfInstitution || '',
        shortNameOfInstitution: school.shortNameOfInstitution || '',
        lowGradeLevelDescriptorId: school.lowGradeLevelDescriptorId || null,
        highGradeLevelDescriptorId: school.highGradeLevelDescriptorId || null,
        schoolTypeDescriptorId: school.schoolTypeDescriptorId || null,
        enrollment: school.enrollment?.toString() || '',
        mainEmailAddress: school.mainEmailAddress || '',
        webSite: school.webSite || '',
        primaryPhone: school.primaryPhone || '',
        streetNumberName: '',
        city: '',
        postalCode: '',
        nameOfCounty: '',
        latitude: null,
        longitude: null
      });
      setAddressQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setExistingAddressId(null);
      setError(null);
      setFieldErrors({});

      // Fetch full school to get address data
      schoolsApi.get(school.schoolId).then((fullSchool) => {
        const primaryAddr = fullSchool.addresses?.find((a: any) => a.isPrimary) || fullSchool.addresses?.[0];
        if (primaryAddr) {
          setFormData(prev => ({
            ...prev,
            streetNumberName: primaryAddr.streetNumberName || '',
            city: primaryAddr.city || '',
            postalCode: primaryAddr.postalCode || '',
            nameOfCounty: primaryAddr.nameOfCounty || '',
            latitude: primaryAddr.latitude || null,
            longitude: primaryAddr.longitude || null
          }));
          setAddressQuery(primaryAddr.streetNumberName || '');
          setExistingAddressId(primaryAddr.educationOrganizationAddressId);
        }
      }).catch((err) => {
        console.warn('Failed to load school address details:', err);
      });
    }
  }, [isOpen, school]);

  // Load descriptors on mount
  useEffect(() => {
    const loadDescriptors = async () => {
      setLoadingDescriptors(true);
      try {
        const [gradeLevels, schoolTypes] = await Promise.all([
          descriptorsApi.getByTypeName('Grade Level'),
          descriptorsApi.getByTypeName('School Type')
        ]);
        setGradeLevelDescriptors(gradeLevels);
        setSchoolTypeDescriptors(schoolTypes);
      } catch (err) {
        console.error('Failed to load descriptors:', err);
      } finally {
        setLoadingDescriptors(false);
      }
    };

    if (isOpen) {
      loadDescriptors();
    }
  }, [isOpen]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Nominatim search
  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 5) { setSuggestions([]); return; }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=us&limit=5`,
        { headers: { 'User-Agent': 'AIU-Directory/1.0 (https://aiu3.net)', 'Accept': 'application/json' } }
      );
      if (response.ok) {
        const data: NominatimResult[] = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (err) { console.error('Nominatim search error:', err); }
    finally { setSearchLoading(false); }
  }, []);

  const handleAddressInputChange = (value: string) => {
    setAddressQuery(value);
    setFormData(prev => ({ ...prev, streetNumberName: value }));
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchAddress(value), 350);
  };

  const handleSelectSuggestion = (suggestion: NominatimResult) => {
    const addr = suggestion.address;
    const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
    const city = addr.city || addr.town || addr.village || '';
    let county = addr.county || '';
    if (county.toLowerCase().endsWith(' county')) county = county.slice(0, -7);

    setFormData(prev => ({
      ...prev,
      streetNumberName: street,
      city,
      postalCode: addr.postcode || '',
      nameOfCounty: county,
      latitude: parseFloat(suggestion.lat),
      longitude: parseFloat(suggestion.lon)
    }));
    setAddressQuery(street);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone format (allow various formats, require 10-11 digits)
  const validatePhone = (phone: string): { valid: boolean; error?: string } => {
    if (!phone) return { valid: true }; // Optional field
    const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
    if (!phoneRegex.test(phone)) {
      return { valid: false, error: 'Phone can only contain numbers, spaces, dashes, parentheses, and dots' };
    }
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return { valid: false, error: 'Phone number must have at least 10 digits' };
    }
    if (digitsOnly.length > 11) {
      return { valid: false, error: 'Phone number cannot exceed 11 digits' };
    }
    return { valid: true };
  };

  // Validate a single field and update errors
  const validateField = (field: string, value: string) => {
    const newErrors = { ...fieldErrors };

    if (field === 'primaryPhone' && value) {
      const result = validatePhone(value);
      if (!result.valid) {
        newErrors.primaryPhone = result.error || 'Invalid phone number';
      } else {
        delete newErrors.primaryPhone;
      }
    }

    if (field === 'mainEmailAddress' && value) {
      if (!validateEmail(value)) {
        newErrors.mainEmailAddress = 'Please enter a valid email address';
      } else {
        delete newErrors.mainEmailAddress;
      }
    }

    setFieldErrors(newErrors);
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.nameOfInstitution.trim()) {
      errors.nameOfInstitution = 'School name is required';
    }

    if (formData.mainEmailAddress && !validateEmail(formData.mainEmailAddress)) {
      errors.mainEmailAddress = 'Please enter a valid email address';
    }

    if (formData.primaryPhone) {
      const phoneResult = validatePhone(formData.primaryPhone);
      if (!phoneResult.valid) {
        errors.primaryPhone = phoneResult.error || 'Invalid phone number';
      }
    }

    if (formData.enrollment && (isNaN(Number(formData.enrollment)) || Number(formData.enrollment) < 0)) {
      errors.enrollment = 'Enrollment must be a positive number';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData: Record<string, any> = {
        nameOfInstitution: formData.nameOfInstitution.trim(),
      };

      // Only include optional fields if they have values
      if (formData.shortNameOfInstitution.trim()) {
        updateData.shortNameOfInstitution = formData.shortNameOfInstitution.trim();
      }
      if (formData.lowGradeLevelDescriptorId) {
        updateData.lowGradeLevelDescriptorId = formData.lowGradeLevelDescriptorId;
      }
      if (formData.highGradeLevelDescriptorId) {
        updateData.highGradeLevelDescriptorId = formData.highGradeLevelDescriptorId;
      }
      if (formData.schoolTypeDescriptorId) {
        updateData.schoolTypeDescriptorId = formData.schoolTypeDescriptorId;
      }
      if (formData.enrollment) {
        updateData.enrollment = Number(formData.enrollment);
      }
      if (formData.mainEmailAddress.trim()) {
        updateData.mainEmailAddress = formData.mainEmailAddress.trim();
      }
      if (formData.webSite.trim()) {
        updateData.webSite = formData.webSite.trim();
      }
      if (formData.primaryPhone.trim()) {
        updateData.primaryPhone = formData.primaryPhone.trim();
      }

      console.log('Sending update data:', JSON.stringify(updateData, null, 2));

      await schoolsApi.update(school.schoolId, updateData);

      // Handle address create/update
      const hasAddressData = formData.streetNumberName.trim() || formData.city.trim();
      if (hasAddressData) {
        const addressData = {
          streetNumberName: formData.streetNumberName.trim() || undefined,
          city: formData.city.trim() || undefined,
          postalCode: formData.postalCode.trim() || undefined,
          nameOfCounty: formData.nameOfCounty.trim() || undefined,
          latitude: formData.latitude,
          longitude: formData.longitude,
          isPrimary: true
        };

        try {
          if (existingAddressId) {
            await schoolsApi.updateAddress(school.schoolId, existingAddressId, addressData);
          } else {
            await schoolsApi.addAddress(school.schoolId, addressData);
          }
        } catch (addrErr) {
          console.warn('School updated but address save failed:', addrErr);
        }
      } else if (existingAddressId && !hasAddressData) {
        // Address fields cleared — remove the address
        try {
          await schoolsApi.removeAddress(school.schoolId, existingAddressId);
        } catch (addrErr) {
          console.warn('School updated but address removal failed:', addrErr);
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      let errorMessage = 'Failed to update school';

      // Log full error for debugging
      console.error('Update school error:', err);
      console.error('Error response:', err?.response);
      console.error('Pydantic errors:', JSON.stringify(err?.response?.errors, null, 2));

      if (err?.response?.errors && Array.isArray(err.response.errors) && err.response.errors.length > 0) {
        // Pydantic validation errors - show field-level details
        const fieldErrors = err.response.errors.map((e: any) => {
          const location = e.loc ? e.loc.join(' > ') : 'Field';
          const message = e.msg || e.message || 'Unknown error';
          const input = e.input !== undefined ? ` (got: ${JSON.stringify(e.input)})` : '';
          return `${location}: ${message}${input}`;
        }).join('; ');
        errorMessage = `Validation error: ${fieldErrors}`;
      } else if (err?.response?.detail) {
        errorMessage = typeof err.response.detail === 'string'
          ? err.response.detail
          : JSON.stringify(err.response.detail, null, 2);
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg">
      <div className="flex flex-col max-h-[85vh]">
        {/* Header - fixed */}
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <HiOfficeBuilding className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Edit School</h3>
              <p className="text-sm text-gray-500">{school.nameOfInstitution}</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 flex-shrink-0">
            <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
              {error}
            </Alert>
          </div>
        )}

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
          {/* Required: School Name */}
          <div>
            <Label htmlFor="nameOfInstitution">
              School Name <span className="text-red-600">*</span>
            </Label>
            <TextInput
              id="nameOfInstitution"
              type="text"
              value={formData.nameOfInstitution}
              onChange={(e) => {
                setFormData({ ...formData, nameOfInstitution: e.target.value });
                if (fieldErrors.nameOfInstitution) {
                  setFieldErrors({ ...fieldErrors, nameOfInstitution: '' });
                }
              }}
              placeholder="e.g., Central Elementary School"
              className={fieldErrors.nameOfInstitution ? 'border-red-500' : ''}
              disabled={loading}
              aria-invalid={!!fieldErrors.nameOfInstitution}
            />
            {fieldErrors.nameOfInstitution && (
              <p className="text-sm text-red-600 mt-1">{fieldErrors.nameOfInstitution}</p>
            )}
          </div>

          {/* Optional: Short Name */}
          <div>
            <Label htmlFor="shortNameOfInstitution">Short Name</Label>
            <TextInput
              id="shortNameOfInstitution"
              type="text"
              value={formData.shortNameOfInstitution}
              onChange={(e) => setFormData({ ...formData, shortNameOfInstitution: e.target.value })}
              placeholder="e.g., Central ES"
              disabled={loading}
            />
          </div>

          {/* Grade Levels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lowGradeLevelDescriptorId">Lowest Grade</Label>
              <Select
                id="lowGradeLevelDescriptorId"
                value={formData.lowGradeLevelDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  lowGradeLevelDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select grade...</option>
                {gradeLevelDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="highGradeLevelDescriptorId">Highest Grade</Label>
              <Select
                id="highGradeLevelDescriptorId"
                value={formData.highGradeLevelDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  highGradeLevelDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select grade...</option>
                {gradeLevelDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* School Type and Enrollment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="schoolTypeDescriptorId">School Type</Label>
              <Select
                id="schoolTypeDescriptorId"
                value={formData.schoolTypeDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  schoolTypeDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading || loadingDescriptors}
              >
                <option value="">Select type...</option>
                {schoolTypeDescriptors.map((descriptor) => (
                  <option key={descriptor.descriptorId} value={descriptor.descriptorId}>
                    {descriptor.shortDescription}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="enrollment">Enrollment</Label>
              <TextInput
                id="enrollment"
                type="number"
                min="0"
                value={formData.enrollment}
                onChange={(e) => {
                  setFormData({ ...formData, enrollment: e.target.value });
                  if (fieldErrors.enrollment) {
                    setFieldErrors({ ...fieldErrors, enrollment: '' });
                  }
                }}
                placeholder="Number of students"
                className={fieldErrors.enrollment ? 'border-red-500' : ''}
                disabled={loading}
                aria-invalid={!!fieldErrors.enrollment}
              />
              {fieldErrors.enrollment && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.enrollment}</p>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="primaryPhone">Main Phone</Label>
              <TextInput
                id="primaryPhone"
                type="tel"
                value={formData.primaryPhone}
                onChange={(e) => {
                  setFormData({ ...formData, primaryPhone: e.target.value });
                  // Clear error as user types (they might be fixing it)
                  if (fieldErrors.primaryPhone) {
                    setFieldErrors({ ...fieldErrors, primaryPhone: '' });
                  }
                }}
                onBlur={(e) => validateField('primaryPhone', e.target.value)}
                placeholder="(412) 555-1234"
                className={fieldErrors.primaryPhone ? 'border-red-500' : ''}
                disabled={loading}
                aria-invalid={!!fieldErrors.primaryPhone}
              />
              {fieldErrors.primaryPhone && (
                <p className="text-sm text-red-600 mt-1">{fieldErrors.primaryPhone}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mainEmailAddress">Main Email</Label>
                <TextInput
                  id="mainEmailAddress"
                  type="email"
                  value={formData.mainEmailAddress}
                  onChange={(e) => {
                    setFormData({ ...formData, mainEmailAddress: e.target.value });
                    if (fieldErrors.mainEmailAddress) {
                      setFieldErrors({ ...fieldErrors, mainEmailAddress: '' });
                    }
                  }}
                  onBlur={(e) => validateField('mainEmailAddress', e.target.value)}
                  placeholder="school@district.edu"
                  className={fieldErrors.mainEmailAddress ? 'border-red-500' : ''}
                  disabled={loading}
                  aria-invalid={!!fieldErrors.mainEmailAddress}
                />
                {fieldErrors.mainEmailAddress && (
                  <p className="text-sm text-red-600 mt-1">{fieldErrors.mainEmailAddress}</p>
                )}
              </div>

              <div>
                <Label htmlFor="webSite">Website</Label>
                <TextInput
                  id="webSite"
                  type="text"
                  value={formData.webSite}
                  onChange={(e) => setFormData({ ...formData, webSite: e.target.value })}
                  placeholder="https://school.district.edu"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Address with Autocomplete */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">School Address</h4>
            <div className="relative" ref={suggestionsRef}>
              <Label htmlFor="streetNumberName">Street Address</Label>
              <TextInput
                id="streetNumberName"
                type="text"
                value={addressQuery}
                onChange={(e) => handleAddressInputChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Start typing an address..."
                disabled={loading}
                autoComplete="off"
              />
              {searchLoading && (
                <div className="absolute right-3 top-8 text-gray-400 text-xs">Searching...</div>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {suggestions.map((s) => (
                    <button
                      key={s.place_id}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      onClick={() => handleSelectSuggestion(s)}
                    >
                      <div className="text-sm text-gray-900 font-medium">
                        {[s.address.house_number, s.address.road].filter(Boolean).join(' ')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[s.address.city || s.address.town || s.address.village, s.address.state, s.address.postcode].filter(Boolean).join(', ')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <TextInput
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Pittsburgh"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="postalCode">ZIP Code</Label>
                <TextInput
                  id="postalCode"
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="15213"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="nameOfCounty">County</Label>
                <TextInput
                  id="nameOfCounty"
                  type="text"
                  value={formData.nameOfCounty}
                  onChange={(e) => setFormData({ ...formData, nameOfCounty: e.target.value })}
                  placeholder="Allegheny"
                  disabled={loading}
                />
              </div>
            </div>
            {formData.latitude && formData.longitude && (
              <p className="text-xs text-gray-400">
                Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </p>
            )}
          </div>

          </div>

          {/* Action Buttons - fixed at bottom */}
          <div className="flex justify-end gap-2 p-6 pt-4 border-t flex-shrink-0">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
